import * as React from "react";
import * as ReactDOM from "react-dom";
import { onLoad } from "spry-react/utils/onLoad";
import { UserAccountsSignInResponse } from "Components/Api/GeneratedDTOs/Api/UserAccounts/UserAccountsSignInResponse";
import { App } from "spry-react/App/app";
import { ErrorDisplayList } from "spry-react/Errors/errorDisplayList";
import { TextField } from "spry-react/Text/textField";
import ReCAPTCHA from "react-google-recaptcha";
import { LabeledCheckboxField } from "spry-react/Checkbox/labeledCheckboxField";
import { ErrorList } from "spry-react/Errors/errorList";
import { FetchWithSerialOperationHandling } from "spry-react/utils/fetchWithSerialOperationHandling";
import { UserAccountsSignInSaveRequest } from "Components/Api/GeneratedDTOs/Api/UserAccounts/UserAccountsSignInSaveRequest";
import { UserAccountsSignInSaveResponse } from "Components/Api/GeneratedDTOs/Api/UserAccounts/UserAccountsSignInSaveResponse";
import { LoginPage } from "Components/Shared/loginPage";
import { configureFetchers } from "shared/Components/Shared/configureFetchers";

interface Props extends UserAccountsSignInResponse {
  fetcher: FetchWithSerialOperationHandling;
}

interface State {
  errors: ErrorList;
  registrationCompleted: boolean;
  username: string;
  password: string;
  recaptchaResponse: string | null;
  requiresRecaptcha: boolean;
  staySignedIn: boolean;
  isPreSignIn: boolean;
}

class SignInPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      errors: props.failedSignIn ? new ErrorList([{ propertyName: "NoProperty", errorMessage: "Failed to verify account." }]) : new ErrorList([]),
      registrationCompleted: props.registrationCompleted,
      username: props.username || "",
      password: "",
      requiresRecaptcha: false,
      recaptchaResponse: null,
      staySignedIn: false,
      isPreSignIn: true,
    };
  }

  render() {
    return (
      <LoginPage
        databaseFriendlyName={this.props.databaseFriendlyName}
        currentYear={this.props.currentYear}
        applicationNamePrefix={this.props.applicationNamePrefix}
        applicationName={this.props.applicationName}
        signInLogoUrl={this.props.signInLogoUrl}
      >
        <form id="login" data-allow-enter onSubmit={this.onSubmit}>
          <ul id="sign-in-container" className="list-unstyled">
            <li id="sign-in-error-message-container">
              <ErrorDisplayList errors={this.state.errors.getList()} />
            </li>
            {this.state.registrationCompleted ? (
              <li className="l-box-vb-1-2x">
                <div>
                  <span style={{ color: "#fff" }}>Registration Successful</span>
                </div>
              </li>
            ) : null}
            <li className="l-box-vb-1-2x">
              <div className="other-party-icon input-wrapper">
                <TextField
                  id="username"
                  value={this.state.username}
                  placeholder={this.props.usernamePlaceholder}
                  onChange={(username: string) => this.setState({ username })}
                  disabled={!this.state.isPreSignIn}
                  autoFocus
                  style={!this.state.isPreSignIn ? { backgroundColor: "#bac4cf" } : {}}
                />
              </div>
            </li>
            {!this.state.isPreSignIn ? (
              <li className="l-box-vb-1-2x">
                <div className="password-icon input-wrapper">
                  <input
                    type="password"
                    id="password"
                    value={this.state.password}
                    placeholder={this.props.passwordPlaceholder}
                    onChange={(e) => {
                      this.setState({ password: e.currentTarget.value });
                    }}
                    autoFocus
                  />
                </div>
              </li>
            ) : null}
            {this.state.requiresRecaptcha ? (
              <li>
                <div className="l-box-v" style={{ float: "right" }}>
                  <ReCAPTCHA
                    ref="recaptcha"
                    sitekey={this.props.recaptchaPublicSiteKey}
                    onChange={(value) => this.setState({ recaptchaResponse: value })}
                  />
                </div>
              </li>
            ) : null}
            <li className="l-box-v">
              <button
                id="sign-in-button"
                className="pure-button pure-button-primary sign-in-button pure-u-1 signin-icon"
                type="submit"
                value="Sign In"
              >
                Sign In
              </button>
            </li>
            <li>
              {!this.state.isPreSignIn ? (
                <div className="pure-g">
                  <div className="pure-u-3-5">
                    <LabeledCheckboxField label="Stay Signed In" value={this.state.staySignedIn} onToggle={this.onStaySignedInToggled} />
                  </div>
                  <div className="pure-u-2-5" style={{ textAlign: "right" }}>
                    <a onClick={() => window.location.reload()}>Sign In as Another User</a>
                  </div>
                </div>
              ) : null}
            </li>
          </ul>
        </form>
      </LoginPage>
    );
  }

  private onStaySignedInToggled = () => {
    this.setState((state) => {
      return { staySignedIn: !state.staySignedIn };
    });
  };

  private onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const data: UserAccountsSignInSaveRequest = {
      userName: this.state.username,
      password: this.state.password,
      staySignedIn: this.state.staySignedIn,
      recaptchaResponse: this.state.recaptchaResponse || "",
      isPreSignIn: this.state.isPreSignIn,
      returnUrl: this.props.returnUrl,
    };

    this.props.fetcher.postJson(this.props.signInSaveUrl, data).then((response: UserAccountsSignInSaveResponse) => {
      if (response.denyPreSignIn) {
        this.setState({ ...this.state, isPreSignIn: false });
      } else if (response.errors && response.errors.length > 0) {
        const errors = new ErrorList(response.errors);
        this.setState({ errors: errors, requiresRecaptcha: response.isRecaptchaRequired });
      } else if (response.samlSignUrl !== null) {
        // If SAML login, go to that link to login
        location.href = response.samlSignUrl;
      } else {
        location.href = this.props.returnUrl;
      }
    });
  };
}

onLoad((data: UserAccountsSignInResponse) => {
  ReactDOM.render(
    <App
      renderChildren={(openDialog, registerBeforeUnload, flashMessage, confirm, startSerialOperation, setWindowLocationHref) => {
        const getCsrfInfo = () => data.csrfInfo;
        const fetchers = configureFetchers(openDialog, startSerialOperation, getCsrfInfo);

        return <SignInPage fetcher={fetchers.serialFetcher} {...data} />;
      }}
    />,
    document.getElementById("root"),
  );
});
